import type { ReactNode } from 'react';

import { isAwaitingAccess } from './pending-access.logic.js';

import { useAuthStore } from '@/stores/auth.store';

/**
 * What a freshly self-registered account sees instead of a dashboard whose
 * every widget answers 403.
 *
 * `/auth/register` assigns `member`, which grants only "read your own
 * notifications" — on purpose, since registration is anonymous. That leaves a
 * real account with nothing to show until an administrator assigns a role, and
 * the honest thing is to say so rather than render an HR dashboard full of
 * failed requests.
 */
export function PendingAccessGate({ children }: { children: ReactNode }): ReactNode {
  const user = useAuthStore((s) => s.user);

  if (!isAwaitingAccess(user?.roles ?? [])) return <>{children}</>;

  return (
    <div className="mx-auto max-w-lg px-4 py-16 text-center">
      <div className="bg-muted/40 rounded-lg border p-8">
        <h1 className="text-xl font-semibold tracking-tight">Your account is ready</h1>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
          You are signed in as <span className="font-medium">{user?.email}</span>, but nobody has
          given this account access to anything yet.
        </p>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
          An administrator needs to assign you a role. Once they do, sign out and back in and your
          screens will appear here.
        </p>
      </div>
    </div>
  );
}
